import React from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Megaphone, Building2, Receipt, Settings as SettingsIcon,
  LogOut, ShieldAlert, Menu,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LOGO_URL } from "../../lib/api";
import AdminLogin from "./AdminLogin";
import Dashboard from "./Dashboard";
import Campaigns from "./Campaigns";
import Partners from "./Partners";
import Transactions from "./Transactions";
import AdminSettings from "./AdminSettings";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["campaigns", "Campaigns", Megaphone],
  ["partners", "Partners", Building2],
  ["transactions", "Ledger", Receipt],
  ["settings", "Settings", SettingsIcon],
];

export default function AdminApp() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B132B]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen bg-[#0B132B] text-white">
      <aside className={`fixed inset-y-0 z-40 w-64 transform border-e border-white/10 bg-[#0B132B] p-5 transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Helping Hands" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
          <div><div className="font-display text-lg font-bold">Helping Hands</div><div className="text-[10px] uppercase tracking-wider text-gold-light">Admin Console</div></div>
        </div>
        <nav className="mt-8 space-y-1">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={label} to={to} end={to === ""} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${isActive ? "bg-emerald-800 text-white" : "text-emerald-100/60 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="h-4.5 w-4.5" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute inset-x-5 bottom-5">
          <div className="rounded-xl border border-white/10 bg-[#1C2541] p-3">
            <div className="truncate text-sm font-semibold">{user.name}</div>
            <div className="truncate text-xs text-emerald-100/50">{user.email}</div>
            <div className="mt-1 inline-block rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-gold-light">{user.role?.replace("_", " ")}</div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-emerald-100/70 hover:bg-white/5"><LogOut className="h-4 w-4" /> Sign Out</button>
        </div>
      </aside>

      <div className="flex-1">
        <header className="flex items-center justify-between border-b border-white/10 px-5 py-4 lg:hidden">
          <button onClick={() => setOpen(!open)}><Menu className="h-6 w-6" /></button>
          <span className="font-display font-bold">Admin</span>
        </header>
        <main className="p-5 lg:p-8">
          {user.role === "read_only" && (
            <div className="mb-5 flex items-center gap-2 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold-light">
              <ShieldAlert className="h-4 w-4" /> You have read-only access. Contact a Super Admin for elevated permissions.
            </div>
          )}
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="partners" element={<Partners />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
